import dayjs from "dayjs";
import {
  formatStockPrice,
  isPenceCurrency,
} from "../../../../utils/stockPriceFormatter";
import type { Tone } from "../../../polish/LabelValueRow";
import type {
  EarningsFeedItem,
  FeedReport,
  MetricPair,
  NotesSummary,
  RecentNote,
  ReportNote,
  ReportPeriod,
  SinceReport,
} from "./types";

// Every figure, day and label the feed and the report page show goes through
// here, so layout code never formats a number and nothing calls .toFixed on
// null.

const finite = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value);

/** "28 Oct 2026": the day a report came out, read as the calendar date it is. */
export const reportedOn = (date: string): string =>
  dayjs(date.slice(0, 10)).format("D MMM YYYY");

/** "Wed 22 Oct": a report or reference date, read as the calendar date it is. */
export const shortDate = (date: string): string =>
  dayjs(date.slice(0, 10)).format("ddd D MMM");

const TIMING: Record<"bmo" | "amc", string> = {
  bmo: "before open",
  amc: "after close",
};

/**
 * "Q3 FY2026" from the API's "Q3 FY2026 · quarter to 30 Sep 2026": the
 * period's name without its end date. A neutral label ("Quarter to 30 Sep
 * 2026", "Half year to …") has no name, so it stays whole.
 */
export const periodTitle = (label: string): string =>
  label.split(" · ")[0] ?? label;

/**
 * The calendar year and month a period ending on this day covers. A 52/53-week
 * year can end a few days into the next month (a quarter "to 2 Jan"), so the
 * end counts back a week first.
 */
const coveredMonth = (end: string): { year: number; month: number } => {
  const date = dayjs(end.slice(0, 10)).subtract(7, "day");
  return { year: date.year(), month: date.month() };
};

/** "FY27" for fiscal year 2027. */
const shortFiscalYear = (year: number): string => `FY${String(year).slice(-2)}`;

/**
 * A card's period block: "Quarter" / "Q3", or "Half year" / "H1", only when
 * FMP numbers the period itself; a neutral period ("Quarter to 30 Sep 2026")
 * has no number to show, so none. When the company's numbering isn't the
 * calendar's (Microsoft's quarter to 30 Sep is its Q1 of FY2027), or the end
 * isn't known, the fiscal year leads: "FY27 Q1". The end is the period's as
 * the API checked it, never the report's raw fiscal date (D39).
 */
export const periodBlock = (
  period: ReportPeriod | null,
): { label: string; value: string } | null => {
  if (period === null) return null;
  const end = period.periodEnd ?? null;
  const covered = end === null ? null : coveredMonth(end);
  const value = (name: string, fiscal: number, calendar: number | null) => {
    const onCalendar =
      calendar === fiscal && period.fiscalYear === covered?.year;
    if (onCalendar || period.fiscalYear === null) return name;
    return `${shortFiscalYear(period.fiscalYear)} ${name}`;
  };
  if (period.fiscalQuarter !== null) {
    const calendarQuarter =
      covered === null ? null : Math.floor(covered.month / 3) + 1;
    return {
      label: "Quarter",
      value: value(
        `Q${period.fiscalQuarter}`,
        period.fiscalQuarter,
        calendarQuarter,
      ),
    };
  }
  const half = /^H([12]) FY/.exec(period.label);
  if (half === null) return null;
  const fiscalHalf = Number(half[1]);
  let calendarHalf: number | null = null;
  if (covered !== null) calendarHalf = covered.month < 6 ? 1 : 2;
  return {
    label: "Half year",
    value: value(`H${fiscalHalf}`, fiscalHalf, calendarHalf),
  };
};

/** "Wed 22 Oct · after close"; the timing word only when FMP states one. */
export const reportLine = (
  reportDate: string,
  callTime: FeedReport["callTime"],
): string => {
  const date = shortDate(reportDate);
  if (callTime === null) return date;
  return `${date} · ${TIMING[callTime]}`;
};

/**
 * "0.48", "-1.25", "0.123": three decimals under 1, so pence-scale figures
 * (BARC.L 0.123 vs 0.1189) don't read as equal; a two-decimal figure keeps
 * its two. A value that rounds to zero shows no sign.
 */
export const formatEps = (value: number): string => {
  const decimals = Math.abs(value) < 1 ? 3 : 2;
  if (Math.abs(value) < 0.5 * 10 ** -decimals) return "0.00";
  const fixed = value.toFixed(decimals);
  if (decimals === 3 && fixed.endsWith("0")) return fixed.slice(0, -1);
  return fixed;
};

/** "28.0B", "812.3M": one decimal by default, kept, so two revenues line up. */
export const formatRevenue = (value: number, decimals = 1): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  let divisor = 1;
  let suffix = "";
  if (abs >= 1e3) [divisor, suffix] = [1e3, "K"];
  if (abs >= 1e6) [divisor, suffix] = [1e6, "M"];
  if (abs >= 1e9) [divisor, suffix] = [1e9, "B"];
  if (abs >= 1e12) [divisor, suffix] = [1e12, "T"];
  return `${sign}${(abs / divisor).toFixed(decimals)}${suffix}`;
};

/** A fraction as "+3.2%"; one that rounds to zero shows no sign. */
export const signedPercent = (fraction: number): string => {
  const points = fraction * 100;
  if (Math.abs(points) < 0.05) return "0.0%";
  const sign = points > 0 ? "+" : "-";
  return `${sign}${Math.abs(points).toFixed(1)}%`;
};

/** A sign's tone: rounds to zero at the shown 1 dp of a percent → flat. */
export const signTone = (fraction: number): Tone => {
  if (Math.abs(fraction * 100) < 0.05) return "flat";
  return fraction > 0 ? "gain" : "loss";
};

/**
 * A price or target in its own currency: "$412.10", "£4.82", "€21.10".
 * Whole targets drop their decimals ("$90"), as the headlines write them.
 */
export const formatPrice = (value: number, currency: string): string => {
  const major = isPenceCurrency(currency) ? value / 100 : value;
  const decimals = Number.isInteger(Math.round(major * 100) / 100) ? 0 : 2;
  return formatStockPrice({ price: value, currency, decimals });
};

/** Exact, for a quote: always two decimals. */
export const formatQuote = (value: number, currency: string): string =>
  formatStockPrice({ price: value, currency });

const currencyCode = (code: string | null): string | null => {
  if (code === null || code.toUpperCase() === "USD") return null;
  return code.toUpperCase();
};

/** Past ±1,000% the estimate sits too near zero for a surprise to mean anything. */
const SURPRISE_LIMIT = 10;

/** (actual − estimate) / |estimate| as a fraction; null without a usable estimate. */
export const surprise = (
  actual: number,
  estimated: number | null,
): number | null => {
  if (estimated === null || estimated === 0) return null;
  const value = (actual - estimated) / Math.abs(estimated);
  return Number.isFinite(value) && Math.abs(value) <= SURPRISE_LIMIT
    ? value
    : null;
};

/**
 * The actual and the estimate as a pair, each formatted the same way. Two
 * figures that only differ past the first precision take one more decimal
 * (up to three), so a bar that is taller never carries the same label.
 */
const pairTexts = (
  actual: number,
  estimated: number | null,
  format: (value: number, extra: number) => string,
): [string, string | null] => {
  if (estimated === null) return [format(actual, 0), null];
  for (let extra = 0; extra < 3; extra += 1) {
    const pair: [string, string] = [
      format(actual, extra),
      format(estimated, extra),
    ];
    if (pair[0] !== pair[1] || actual === estimated) return pair;
  }
  return [format(actual, 2), format(estimated, 2)];
};

const epsAt = (value: number, extra: number): string => {
  if (extra === 0) return formatEps(value);
  const decimals = (Math.abs(value) < 1 ? 3 : 2) + extra;
  return value.toFixed(Math.min(decimals, 4));
};

const revenueAt = (value: number, extra: number): string =>
  formatRevenue(value, 1 + extra);

export interface MetricComparison {
  key: "eps" | "revenue";
  label: string;
  /** The reporting currency when it isn't USD. */
  currency: string | null;
  actual: number;
  estimated: number | null;
  actualText: string;
  estimatedText: string | null;
  /** Beat (gain), miss (loss) or in line (flat); null without an estimate. */
  tone: Tone | null;
  /** Null without a usable estimate. */
  surprise: number | null;
}

const comparison = (
  key: MetricComparison["key"],
  label: string,
  pair: MetricPair,
  currency: string | null,
  format: (value: number, extra: number) => string,
): MetricComparison | null => {
  if (pair === null || !finite(pair.actual)) return null;
  const estimated = finite(pair.estimated) ? pair.estimated : null;
  const [actualText, estimatedText] = pairTexts(pair.actual, estimated, format);
  const change = surprise(pair.actual, estimated);
  let tone: Tone | null = null;
  if (estimated !== null && change !== null) tone = signTone(change);
  if (estimated !== null && change === null)
    tone =
      actualText === estimatedText
        ? "flat"
        : pair.actual > estimated
          ? "gain"
          : "loss";
  return {
    key,
    label,
    currency,
    actual: pair.actual,
    estimated,
    actualText,
    estimatedText,
    tone,
    surprise: change,
  };
};

/** EPS and revenue against their estimates, each only when it has an actual. */
export const metricComparisons = (source: {
  eps: MetricPair;
  revenue: MetricPair;
  reportingCurrency: string | null;
}): MetricComparison[] => {
  const currency = currencyCode(source.reportingCurrency);
  return [
    comparison("eps", "EPS", source.eps, currency, epsAt),
    comparison("revenue", "Revenue", source.revenue, currency, revenueAt),
  ].filter((item): item is MetricComparison => item !== null);
};

/** "EPS", or "EPS · GBP" when the reporting currency isn't USD. */
export const metricLabel = (metric: MetricComparison): string =>
  metric.currency === null
    ? metric.label
    : `${metric.label} · ${metric.currency}`;

/** "EPS 0.84 vs 0.76": a previous report's one-line figure. */
export const metricLine = (metric: MetricComparison): string => {
  const label = metricLabel(metric);
  if (metric.estimatedText === null) return `${label} ${metric.actualText}`;
  return `${label} ${metric.actualText} vs ${metric.estimatedText}`;
};

/** "Since Tue 21 Oct close · $386.20 → $412.10", or null without a return. */
export const sinceHeaderLine = (
  since: SinceReport | null,
  priceCurrency: string,
): string | null => {
  if (since === null) return null;
  if (!finite(since.referenceClose) || !finite(since.price)) return null;
  return `Since ${shortDate(since.referenceDate)} close · ${formatQuote(
    since.referenceClose,
    priceCurrency,
  )} → ${formatQuote(since.price, priceCurrency)}`;
};

type TargetFields = Pick<
  ReportNote,
  "priceTarget" | "previousPriceTarget" | "targetAction" | "targetCurrency"
>;

/** The target's change as a fraction, when both ends are known. */
export const targetChange = (note: TargetFields): number | null => {
  if (!finite(note.priceTarget) || !finite(note.previousPriceTarget))
    return null;
  if (note.previousPriceTarget <= 0) return null;
  return note.priceTarget / note.previousPriceTarget - 1;
};

/** Raised gain, cut loss, kept or set flat; null with no target. */
const targetTone = (note: TargetFields): Tone | null => {
  if (!finite(note.priceTarget)) return null;
  if (note.targetAction === "raised") return "gain";
  if (note.targetAction === "lowered") return "loss";
  return "flat";
};

const ratingTone = (note: Pick<ReportNote, "gradeAction">): Tone | null => {
  if (note.gradeAction === "upgrade") return "gain";
  if (note.gradeAction === "downgrade") return "loss";
  return null;
};

/** "10:56": FMP's published time on the reader's clock. */
export const timeOf = (iso: string): string => dayjs(iso).format("HH:mm");

/** "29 Oct": the reader's day of an instant, with the year when it isn't this one. */
export const noteDateOf = (iso: string, now: number = Date.now()): string => {
  const date = dayjs(iso);
  return date.year() === dayjs(now).year()
    ? date.format("D MMM")
    : date.format("D MMM YYYY");
};

export interface NoteRow {
  id: string;
  firm: string;
  /** Another listing's ticker: a SHEL note on a SHEL.L card. */
  tickerTag: string | null;
  /** The rating after the note; null when the note gave none. */
  rating: string | null;
  /** The rating before, only when it moved. */
  previousRating: string | null;
  /** Upgrade gain, downgrade loss, otherwise null. */
  ratingTone: Tone | null;
  initiated: boolean;
  /** The target before, when the note or an earlier one told us. */
  targetBefore: string | null;
  targetAfter: string | null;
  targetTone: Tone | null;
  /** The target's change as a fraction, when both ends are known. */
  change: number | null;
  /** "10:56". */
  time: string;
  /** The reader's day, "29 Oct". */
  date: string;
  sourceUrl: string;
  publisher: string;
}

/** The before and after targets as shown: "$600" and "$650", each null when unknown. */
const targetTexts = (
  note: TargetFields,
): Pick<NoteRow, "targetBefore" | "targetAfter"> => ({
  targetBefore:
    finite(note.priceTarget) && finite(note.previousPriceTarget)
      ? formatPrice(note.previousPriceTarget, note.targetCurrency)
      : null,
  targetAfter: finite(note.priceTarget)
    ? formatPrice(note.priceTarget, note.targetCurrency)
    : null,
});

/** One note's texts; `symbol` is the listing the page shows. */
export const noteRow = (note: ReportNote, symbol: string): NoteRow => {
  const moved =
    note.gradeNew !== null &&
    note.gradePrevious !== null &&
    note.gradePrevious !== note.gradeNew;
  return {
    id: note.id,
    firm: note.firm,
    tickerTag: note.quotedSymbol !== symbol ? note.quotedSymbol : null,
    rating: note.gradeNew,
    previousRating: moved ? note.gradePrevious : null,
    ratingTone: ratingTone(note),
    initiated: note.gradeAction === "initiate",
    ...targetTexts(note),
    targetTone: targetTone(note),
    change: targetChange(note),
    time: timeOf(note.feedAt),
    date: noteDateOf(note.feedAt),
    sourceUrl: note.sourceUrl,
    publisher: note.publisher,
  };
};

/** What a note says, as one sentence for a screen reader. */
export const noteSummary = (row: NoteRow): string => {
  const parts = [row.firm];
  if (row.rating !== null)
    parts.push(
      row.previousRating === null
        ? row.rating
        : `${row.previousRating} to ${row.rating}`,
    );
  if (row.targetAfter !== null)
    parts.push(
      row.targetBefore === null
        ? `target ${row.targetAfter}`
        : `target ${row.targetBefore} to ${row.targetAfter}`,
    );
  if (row.change !== null) parts.push(signedPercent(row.change));
  return parts.join(", ");
};

/** A card's React key and Back anchor: its report's event id. */
export const itemKey = (item: EarningsFeedItem): string => item.id;

/** The loaded pages as one list, each card once (a report that moved between two fetches shows in its first place). */
export const flattenPages = (
  pages: readonly { data: EarningsFeedItem[] }[],
): EarningsFeedItem[] => {
  const seen = new Set<string>();
  const items: EarningsFeedItem[] = [];
  pages.forEach((page) =>
    page.data.forEach((item) => {
      const key = itemKey(item);
      if (seen.has(key)) return;
      seen.add(key);
      items.push(item);
    }),
  );
  return items;
};

/** The call slot's place within a report day, as the API orders it: after close, no time, before open. */
const callSlot = (callTime: FeedReport["callTime"]): number => {
  if (callTime === "amc") return 0;
  if (callTime === "bmo") return 2;
  return 1;
};

/** Byte order, as the API's "C" collation (and stable whatever the reader's locale). */
const byteOrder = (a: string, b: string): number => {
  if (a === b) return 0;
  return a < b ? -1 : 1;
};

/** The feed's order, newest report first: report date DESC, call slot, symbol, event id. */
export const compareCards = (
  a: EarningsFeedItem,
  b: EarningsFeedItem,
): number =>
  byteOrder(b.report.reportDate, a.report.reportDate) ||
  callSlot(a.report.callTime) - callSlot(b.report.callTime) ||
  byteOrder(a.company.symbol, b.company.symbol) ||
  byteOrder(a.id, b.id);

/**
 * How many of the head's reports aren't on screen yet: a card that has just
 * appeared, at the top or within what's shown. A head card that sorts after
 * the last one shown is only the next page's, so it doesn't count.
 */
export const countNew = (
  head: readonly EarningsFeedItem[] | undefined,
  shown: readonly EarningsFeedItem[],
): number => {
  if (head === undefined) return 0;
  const last = shown[shown.length - 1];
  if (last === undefined) return head.length;
  const ids = new Set(shown.map(itemKey));
  return head.filter(
    (item) => !ids.has(itemKey(item)) && compareCards(item, last) < 0,
  ).length;
};

/** "3 new"; a whole page of them reads "20+ new". */
export const newItemsLabel = (count: number, pageSize: number): string =>
  count >= pageSize ? `${pageSize}+ new` : `${count} new`;

/** "Today", "Yesterday", "Wed 22 Oct", with the year when it isn't this one. */
export const dayLabel = (day: string, now: number): string => {
  const today = dayjs(now);
  if (day === today.format("YYYY-MM-DD")) return "Today";
  if (day === today.subtract(1, "day").format("YYYY-MM-DD")) return "Yesterday";
  const date = dayjs(day);
  if (date.year() === today.year()) return date.format("ddd D MMM");
  return date.format("ddd D MMM YYYY");
};

export interface DayGroup<T> {
  day: string;
  label: string;
  items: T[];
}

const groupBy = <T>(
  items: readonly T[],
  dayOf: (item: T) => string,
  now: number,
): DayGroup<T>[] => {
  const groups: DayGroup<T>[] = [];
  items.forEach((item) => {
    const day = dayOf(item);
    const last = groups[groups.length - 1];
    if (last !== undefined && last.day === day) {
      last.items.push(item);
      return;
    }
    groups.push({ day, label: dayLabel(day, now), items: [item] });
  });
  return groups;
};

/** Consecutive cards on the same report date, read as the calendar date it is. */
export const groupByReportDay = (
  items: readonly EarningsFeedItem[],
  now: number,
): DayGroup<EarningsFeedItem>[] =>
  groupBy(items, (item) => item.report.reportDate.slice(0, 10), now);

export interface SummaryPart {
  key: keyof NotesSummary;
  /** "↑" raised, "↓" cut, "⇧" upgrade, "⇩" downgrade; none for initiations and firms. */
  glyph: string | null;
  count: number;
  /** "raised", "upgrade" / "upgrades", "firm" / "firms". */
  label: string;
  /** The glyph's tone: up gain, down loss. */
  tone: Tone | null;
}

const SUMMARY: {
  key: Exclude<keyof NotesSummary, "total">;
  glyph: string | null;
  one: string;
  many: string;
  tone: Tone | null;
}[] = [
  { key: "raised", glyph: "↑", one: "raised", many: "raised", tone: "gain" },
  { key: "cut", glyph: "↓", one: "cut", many: "cut", tone: "loss" },
  {
    key: "upgrades",
    glyph: "⇧",
    one: "upgrade",
    many: "upgrades",
    tone: "gain",
  },
  {
    key: "downgrades",
    glyph: "⇩",
    one: "downgrade",
    many: "downgrades",
    tone: "loss",
  },
  {
    key: "initiations",
    glyph: null,
    one: "initiation",
    many: "initiations",
    tone: null,
  },
  { key: "firms", glyph: null, one: "firm", many: "firms", tone: null },
];

/** The card's summary line: each non-zero count with its glyph and word, firms last. */
export const summaryParts = (summary: NotesSummary): SummaryPart[] =>
  summary.total === 0
    ? []
    : SUMMARY.filter((part) => summary[part.key] > 0).map((part) => ({
        key: part.key,
        glyph: part.glyph,
        count: summary[part.key],
        label: summary[part.key] === 1 ? part.one : part.many,
        tone: part.tone,
      }));

/** The counts in words, for the card button's name: "12 raised, 3 cut, 1 downgrade, 14 firms". */
export const summaryText = (summary: NotesSummary): string =>
  summaryParts(summary)
    .map((part) => `${part.count} ${part.label}`)
    .join(", ");

export type RecentRow = Pick<
  NoteRow,
  | "id"
  | "firm"
  | "rating"
  | "previousRating"
  | "ratingTone"
  | "initiated"
  | "targetBefore"
  | "targetAfter"
  | "targetTone"
>;

/** A card's note row, toned as the report page's notes table: firm, rating, target from → to. */
export const recentRow = (note: RecentNote): RecentRow => ({
  id: note.id,
  firm: note.firm,
  rating: note.gradeNew,
  previousRating: null,
  ratingTone: ratingTone(note),
  initiated: note.gradeAction === "initiate",
  ...targetTexts(note),
  targetTone: targetTone(note),
});

export interface TargetMove {
  id: string;
  firm: string;
  before: number | null;
  after: number;
  tone: Tone;
  row: NoteRow;
}

export interface TargetMoves {
  moves: TargetMove[];
  /** The plot's ends: every shown before and after target. */
  low: number;
  high: number;
}

/**
 * Each firm's latest target move, highest target first. Only the targets in
 * the most common currency are drawn on the one scale; the notes table still
 * lists every note.
 */
export const targetMoves = (
  notes: readonly ReportNote[],
  symbol: string,
): TargetMoves => {
  const latest = new Map<string, ReportNote>();
  notes.forEach((note) => {
    if (!finite(note.priceTarget) || latest.has(note.firmKey)) return;
    latest.set(note.firmKey, note);
  });
  const withTargets = Array.from(latest.values());
  const counts = new Map<string, number>();
  withTargets.forEach((note) =>
    counts.set(note.targetCurrency, (counts.get(note.targetCurrency) ?? 0) + 1),
  );
  let currency: string | null = null;
  let most = 0;
  withTargets.forEach((note) => {
    const count = counts.get(note.targetCurrency) ?? 0;
    if (count > most) [currency, most] = [note.targetCurrency, count];
  });
  const moves = withTargets
    .filter((note) => note.targetCurrency === currency)
    .map((note): TargetMove => {
      const row = noteRow(note, symbol);
      return {
        id: note.id,
        firm: note.firm,
        before: finite(note.previousPriceTarget)
          ? note.previousPriceTarget
          : null,
        after: note.priceTarget as number,
        tone: row.targetTone ?? "flat",
        row,
      };
    })
    .sort((a, b) => b.after - a.after);
  const values = moves.flatMap((move) =>
    move.before === null ? [move.after] : [move.before, move.after],
  );
  return {
    moves,
    low: values.length > 0 ? Math.min(...values) : 0,
    high: values.length > 0 ? Math.max(...values) : 0,
  };
};
